import { useEffect, useMemo, useRef } from 'react';
import type {
    ResourceBonusGridFilters,
    ResourceBonusGridSort,
    ResourceBonusSortDirection,
    ResourceBonusSortKey
} from './resourceHunterUtils';

type ResourceBonusSortOption = {
    value: ResourceBonusSortKey;
    label: string;
};

const DEFAULT_SORT_OPTIONS: ResourceBonusSortOption[] = [
    { value: 'name', label: 'Name' },
    { value: 'level', label: 'Level' },
    { value: 'expansion', label: 'Expansion' }
];

type ResourceBonusGridControlsProps = {
    filters: ResourceBonusGridFilters;
    sort: ResourceBonusGridSort;
    expansionOptions: string[];
    zoneTypeOptions: string[];
    visibleCount: number;
    totalCount: number;
    hasActiveFilters?: boolean;
    onFiltersChange: (filters: ResourceBonusGridFilters) => void;
    onSortChange: (sort: ResourceBonusGridSort) => void;
    onResetFilters?: () => void;
    sortOptions?: ResourceBonusSortOption[];
};

export function ResourceBonusGridControls({
    filters,
    sort,
    expansionOptions,
    zoneTypeOptions,
    visibleCount,
    totalCount,
    hasActiveFilters = false,
    onFiltersChange,
    onSortChange,
    onResetFilters,
    sortOptions = DEFAULT_SORT_OPTIONS
}: ResourceBonusGridControlsProps) {
    function updateFilters(next: Partial<ResourceBonusGridFilters>) {
        onFiltersChange({ ...filters, ...next });
    }

    function updateSort(next: Partial<ResourceBonusGridSort>) {
        onSortChange({ ...sort, ...next });
    }

    const expansionSelectRef = useRef<HTMLSelectElement | null>(null);

    const normalizedExpansionSelection = useMemo(() => filters.expansions.map((value) => value.trim()).filter(Boolean), [filters.expansions]);

    useEffect(() => {
        const select = expansionSelectRef.current;
        if (!select) {
            return;
        }

        for (const option of select.options) {
            option.selected = normalizedExpansionSelection.includes(option.value);
        }
    }, [normalizedExpansionSelection, expansionOptions]);

    return (
        <section className="resource-grid-controls" aria-label="Filter and sort zone bonuses">
            <div className="resource-grid-controls__fields">
                <label className="resource-grid-control">
                    <span>Expansions</span>
                    <select
                        className="form-select form-select-sm"
                        multiple
                        ref={expansionSelectRef}
                        defaultValue={normalizedExpansionSelection}
                        onChange={(event) => {
                            const values = Array.from(event.target.selectedOptions, (option) => option.value);
                            updateFilters({ expansions: values });
                        }}
                        aria-label="Select one or more expansions"
                    >
                        {expansionOptions.map((expansion) => (
                            <option key={expansion} value={expansion}>
                                {expansion}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="resource-grid-control">
                    <span>Indoor/Outdoor</span>
                    <select className="form-select form-select-sm" value={filters.zoneType} onChange={(event) => updateFilters({ zoneType: event.target.value })}>
                        <option value="">All zone types</option>
                        {zoneTypeOptions.map((zoneType) => (
                            <option key={zoneType} value={zoneType}>
                                {zoneType}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="resource-grid-control resource-grid-control--level">
                    <span>Min level</span>
                    <input
                        className="form-control form-control-sm"
                        inputMode="numeric"
                        min="1"
                        placeholder="Any"
                        type="number"
                        value={filters.minLevel}
                        onChange={(event) => updateFilters({ minLevel: event.target.value })}
                    />
                </label>

                <label className="resource-grid-control resource-grid-control--level">
                    <span>Max level</span>
                    <input
                        className="form-control form-control-sm"
                        inputMode="numeric"
                        min="1"
                        placeholder="Any"
                        type="number"
                        value={filters.maxLevel}
                        onChange={(event) => updateFilters({ maxLevel: event.target.value })}
                    />
                </label>

                <label className="resource-grid-control">
                    <span>Sort by</span>
                    <select className="form-select form-select-sm" value={sort.sortBy} onChange={(event) => updateSort({ sortBy: event.target.value as ResourceBonusSortKey })}>
                        {sortOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="resource-grid-control">
                    <span>Order</span>
                    <select className="form-select form-select-sm" value={sort.direction} onChange={(event) => updateSort({ direction: event.target.value as ResourceBonusSortDirection })}>
                        <option value="asc">Ascending</option>
                        <option value="desc">Descending</option>
                    </select>
                </label>
            </div>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                <p className="small text-muted mb-0">
                    Showing {visibleCount} of {totalCount} zone{totalCount === 1 ? '' : 's'}.
                </p>
                {onResetFilters && hasActiveFilters ? (
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={onResetFilters}
                    >
                        Clear filters
                    </button>
                ) : null}
            </div>
        </section>
    );
}
